'use client'

import { useEffect, useRef, useState } from 'react'
import { FadeIn } from '@/components/FadeIn'
import { Button } from './Button'
import { trackEvent } from '@/lib/umami'

export function CalculatorWithCallToAction() {
  const customersInputId = 'calculator-customers'
  const priceInputId = 'calculator-price'
  const [customers, setCustomers] = useState('1000')
  const [price, setPrice] = useState('150')

  const customersValue = parseFloat(customers) || 0
  const priceValue = parseFloat(price) || 0

  const revenue = customersValue * priceValue
  const cashBackFromInsurance = revenue * 0.8

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(num)
  }

  const formatNumberInput = (num: number | string) => {
    if (!num || num === '') return ''
    const numValue =
      typeof num === 'string'
        ? parseFloat(num.replace(/\./g, '').replace(',', '.')) || 0
        : num
    if (numValue === 0) return ''
    return new Intl.NumberFormat('de-DE', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(numValue)
  }

  const parseFormattedNumber = (value: string) => {
    return value.replace(/\./g, '').replace(',', '.')
  }

  const hasInteracted = useRef(false)

  const handleCustomersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = parseFormattedNumber(e.target.value)
    if (rawValue === '' || /^\d*$/.test(rawValue)) {
      hasInteracted.current = true
      setCustomers(rawValue)
    }
  }

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = parseFormattedNumber(e.target.value)
    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
      hasInteracted.current = true
      setPrice(rawValue)
    }
  }

  useEffect(() => {
    if (!hasInteracted.current) return

    const timeout = window.setTimeout(() => {
      trackEvent('calculator_use', {
        customers: customersValue,
        price: priceValue,
        revenue,
        cashback: cashBackFromInsurance,
      })
    }, 800)

    return () => window.clearTimeout(timeout)
  }, [customersValue, priceValue, revenue, cashBackFromInsurance])

  return (
    <div className="rounded-3xl border border-zinc-200 bg-zinc-50 p-6 sm:p-8">
      <FadeIn>
        <h2 className="font-display text-2xl font-semibold text-zinc-950 sm:text-3xl">
          Potential-Rechner
        </h2>
      </FadeIn>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <FadeIn>
          <div className="group relative z-0">
            <input
              type="text"
              id={customersInputId}
              value={customers ? formatNumberInput(customers) : ''}
              onChange={handleCustomersChange}
              placeholder=" "
              className="peer block w-full rounded-xl border border-zinc-300 bg-white px-4 pt-12 pb-4 text-sm text-zinc-950 ring-4 ring-transparent transition focus:border-zinc-950 focus:ring-zinc-950/5 focus:outline-hidden"
            />
            <label
              htmlFor={customersInputId}
              className="pointer-events-none absolute top-1/2 left-4 -mt-3 origin-left text-sm leading-none tracking-tighter text-zinc-500 transition-all duration-200 peer-not-placeholder-shown:-translate-y-4 peer-not-placeholder-shown:scale-75 peer-not-placeholder-shown:font-semibold peer-not-placeholder-shown:text-zinc-950 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-zinc-950"
            >
              Deine Kunden (p.a.)
            </label>
            <div className="absolute top-0 left-0 -z-10 hidden h-full w-full animate-pulse bg-cyan-700/50 blur-sm duration-1000 group-focus-within:block" />
          </div>
        </FadeIn>

        <FadeIn>
          <div className="group relative z-0">
            <input
              type="text"
              id={priceInputId}
              value={price ? formatNumberInput(price) : ''}
              onChange={handlePriceChange}
              placeholder=" "
              className="peer block w-full rounded-xl border border-zinc-300 bg-white px-4 pt-12 pb-4 text-sm text-zinc-950 ring-4 ring-transparent transition focus:border-zinc-950 focus:ring-zinc-950/5 focus:outline-hidden"
            />
            <label
              htmlFor={priceInputId}
              className="tracking-tighter pointer-events-none absolute top-1/2 left-4 -mt-3 origin-left text-sm leading-none text-zinc-500 transition-all duration-200 peer-not-placeholder-shown:-translate-y-4 peer-not-placeholder-shown:scale-75 peer-not-placeholder-shown:font-semibold peer-not-placeholder-shown:text-zinc-950 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-zinc-950"
            >
              Dein Preis (€)
            </label>
          </div>
        </FadeIn>
      </div>

      <FadeIn className="mt-6">
        <div className="rounded-2xl bg-white p-6 ring-1 ring-zinc-950/5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs font-semibold text-zinc-500">
                Dein Gesamtumsatz
              </p>
              <p className="mt-1 text-2xl font-semibold text-zinc-950">
                {formatNumber(revenue)}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-zinc-900">
                GKV-Cashback
              </p>
              <p className="mt-1 text-2xl font-semibold text-cyan-600">
                {formatNumber(cashBackFromInsurance)}
              </p>
              <p className="mt-0.5 text-xs text-zinc-500">
                (meist 80-100%) von der Krankenkasse direkt aufs Konto deiner
                Kunden
              </p>
            </div>
          </div>
        </div>
      </FadeIn>
      <p className="mt-4 text-xs text-zinc-500">
        <span className="font-semibold">
          Durchschittlich 120 € pro Teilnahme
        </span>{' '}
        werden von der Krankenkasse erstattet (Quelle: GKV-Spitzenverband,
        Präventionsbericht 2024). GKVs übernehmen{' '}
        <span className="font-semibold">bis zu 100 % der Kosten</span> für dein
        Programm meist <span className="font-semibold">2x pro Jahr</span>. Diese
        Berechnung ist nur eine Schätzung. Die tatsächlichen Ergebnisse können
        abweichen.
      </p>
      <div className="mt-8 flex flex-col items-center gap-4">
        {revenue > 10000 ? (
          <Button
            href="/contact"
            className="w-fit text-center"
            event="cta_contact"
            eventData={{
              location: 'calculator',
              customers: customersValue,
              price: priceValue,
              revenue,
            }}
          >
            Kontakt aufnehmen
          </Button>
        ) : (
          <>
            <p className="text-center text-sm text-zinc-900">
              Puh, das Projekt lohnt sich für dich eher nicht...
            </p>
            <Button
              href="https://zpp-konzepte.de"
              target="_blank"
              className="w-fit text-center"
              event="calculator_cta"
              eventData={{
                destination: 'zpp',
                customers: customersValue,
                price: priceValue,
                revenue,
              }}
            >
              Vorort Angebote passen da besser &rarr;
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
